// Pure Voice Ledger range math: turns the server's day-bucketed spoken
// seconds into the today / week / month / year / all-time numbers the card
// and the Progress page render. No Firebase imports, so it is fully unit
// testable.
//
// Every boundary is evaluated in the user's ACCOUNTING timezone
// (users/{uid}/timezone), never the device timezone. The backend keys each
// bucket by the message createdAt rendered in that same zone, so a traveling
// user (or a stale profile zone) would otherwise look up the wrong key and
// see a false zero for today.
//
// Spec: docs/superpowers/specs/2026-07-23-voice-ledger-design.md

const KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Accepted by zoneinfo but not a canonical Intl zone name, and both name
// plain UTC, so honouring them costs nothing.
const ALWAYS_SERVER_COMPATIBLE = new Set(["UTC", "GMT"]);

export const UTC = "UTC";

/**
 * True when the backend's zoneinfo would accept this exact spelling.
 *
 * The test is whether the name is ALREADY the canonical one: Intl reports the
 * canonical zone a name resolves to, so a canonical name comes back
 * unchanged, while a miscased spelling ("america/new_york", "America/New_york")
 * and a link alias ("US/Eastern", "Europe/Kyiv") both come back as something
 * else. Every canonical name was checked against the deployed tzdata and all
 * are accepted, which makes this a provable never-accept-what-the-server-
 * rejects rule - and unlike enumerating Intl.supportedValuesOf, it works on
 * the iOS 15.0-15.3 WebViews that lack that API, where a looser fallback
 * would reintroduce exactly this bug.
 *
 * Link aliases are the deliberate cost: zoneinfo does resolve them, so a
 * profile holding one falls back to UTC on the client while the server
 * buckets in the alias's zone. Their casing cannot be validated from
 * JavaScript - accepting them by shape is what let through
 * "Canada/East-saskatchewan" and "US/Pacific-New", which tzdata rejects - and
 * the app itself only ever writes a canonical zone from resolvedOptions(), so
 * an alias can only arrive by hand-editing the RTDB node.
 */
function isServerCompatibleZone(tzName) {
  if (ALWAYS_SERVER_COMPATIBLE.has(tzName)) return true;
  try {
    return (
      new Intl.DateTimeFormat("en-CA", { timeZone: tzName }).resolvedOptions()
        .timeZone === tzName
    );
  } catch {
    return false;
  }
}

/**
 * users/{uid}/timezone is user-writable and unvalidated, so invalid values
 * (not just missing ones) must fall back to UTC - identically to the backend
 * helpers, so client and server always resolve the same bucket key.
 *
 * "Identically" is the hard part: Intl matches IANA names case-insensitively
 * while Python's zoneinfo, which keys the server's buckets, does not. Taking
 * Intl at its word would have the client reading "America/New_York" keys for
 * a profile value of "america/new_york" whose buckets the server wrote under
 * UTC - a false zero for today around midnight. So a name is accepted only
 * when the backend is known to accept that exact spelling; see
 * isServerCompatibleZone for the rule and what it deliberately gives up.
 *
 * @param {unknown} tzName
 * @returns {string} a usable IANA zone name
 */
export function resolveTimeZone(tzName) {
  if (typeof tzName !== "string" || !tzName) return UTC;
  if (!isServerCompatibleZone(tzName)) return UTC;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: tzName });
    return tzName;
  } catch {
    return UTC;
  }
}

/**
 * Renders an instant as the YYYY-MM-DD bucket key of the given zone.
 * Parts are assembled by hand rather than read off a formatted string: the
 * key format is a data contract with the backend, and it must not depend on
 * how any given ICU build punctuates a locale's short date.
 * @param {number} epochMs
 * @param {string} timeZone
 * @returns {string} "YYYY-MM-DD"
 */
export function dateKeyFor(epochMs, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: resolveTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(epochMs));
  const value = (type) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

// Bucket keys are fixed-width ISO dates, so plain string comparison is
// calendar comparison. Dates are constructed in UTC purely as calendar
// arithmetic on those keys - no zone conversion happens here.
function keyToUtcDate(key) {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function mondayKeyOf(key) {
  const date = keyToUtcDate(key);
  const offset = (date.getUTCDay() + 6) % 7; // Mon=0 ... Sun=6
  date.setUTCDate(date.getUTCDate() - offset);
  return date.toISOString().slice(0, 10);
}

/**
 * The oldest bucket key the client needs to fetch: the earlier of Jan 1 and
 * the Monday of the current ISO week, since a week can span the New Year.
 * At most ~366 tiny nodes.
 * @param {number} nowMs
 * @param {string} timeZone
 * @returns {string} "YYYY-MM-DD"
 */
export function fetchStartKey(nowMs, timeZone) {
  const todayKey = dateKeyFor(nowMs, timeZone);
  const jan1 = `${todayKey.slice(0, 4)}-01-01`;
  const monday = mondayKeyOf(todayKey);
  return monday < jan1 ? monday : jan1;
}

/**
 * @param {object} input
 * @param {Record<string, {spokenSeconds: number}>} input.buckets raw RTDB shape
 * @param {number} input.lifetimeSeconds stats/{uid}/{lang}/lifetimeSpokenSeconds
 * @param {number} input.nowMs
 * @param {string} input.timeZone accounting timezone
 * @returns {{todaySeconds: number, weekSeconds: number, monthSeconds: number,
 *   yearSeconds: number, allTimeSeconds: number, practiceDaysThisWeek: number}}
 */
export function computeRanges({ buckets, lifetimeSeconds, nowMs, timeZone }) {
  const todayKey = dateKeyFor(nowMs, timeZone);
  const mondayKey = mondayKeyOf(todayKey);
  const monthPrefix = todayKey.slice(0, 7);
  const yearPrefix = todayKey.slice(0, 4);

  let todaySeconds = 0;
  let weekSeconds = 0;
  let monthSeconds = 0;
  let yearSeconds = 0;
  let practiceDaysThisWeek = 0;

  const entries =
    buckets && typeof buckets === "object" ? Object.entries(buckets) : [];

  for (const [key, value] of entries) {
    if (!KEY_PATTERN.test(key)) continue;
    // Future-dated keys can only come from clock skew or bad source data.
    // They are excluded from EVERY range: letting the prefix-matched ranges
    // count them while the week range (bounded by today) could not would make
    // the month exceed the sum of its weeks.
    if (key > todayKey) continue;
    const seconds = Number(value?.spokenSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) continue;

    if (key === todayKey) todaySeconds += seconds;
    if (key >= mondayKey) {
      weekSeconds += seconds;
      practiceDaysThisWeek += 1; // one bucket per calendar day
    }
    if (key.startsWith(monthPrefix)) monthSeconds += seconds;
    if (key.startsWith(yearPrefix)) yearSeconds += seconds;
  }

  const lifetime = Number(lifetimeSeconds);

  return {
    todaySeconds,
    weekSeconds,
    monthSeconds,
    yearSeconds,
    // All-time reads the additive lifetime counter, floored at this year's
    // sum: the counter and the buckets are healed independently, and showing
    // an all-time total below the year total would read as broken on a
    // surface whose whole promise is honest numbers.
    allTimeSeconds: Math.max(
      Number.isFinite(lifetime) && lifetime > 0 ? lifetime : 0,
      yearSeconds
    ),
    practiceDaysThisWeek,
  };
}
